import React, { useEffect, useRef } from 'react';
import { fmtMoney, fmtDelta, priceSourceLabel } from '../../utils/priceMath';
import { chipLabel, groupReadiness } from '../../utils/tmPricing';

/**
 * The last look before Approve T&M Pricing: what was ordered and what will bill, per line, at
 * which unit price; the header money, tax and total; and every warning / acknowledgement. All
 * figures are the Preview's own strings (the server's), never recomputed here.
 * blocked: why this Preview can no longer be approved (e.g. a re-read of the cost data landed after
 * it): Approve is disabled and the reason is shown; Back leads to a new Preview.
 */
export default function TmApproveConfirm({ orderNumber, preview, rows, busy, onBack, onApprove, blocked = null }) {
  const p = (preview && preview.proposal) || {};
  const h = p.header || {};
  const c = p.computed || {};
  const g = groupReadiness(preview && preview.readiness, {});
  // The Approve button that opened this step is gone: put focus on the question instead of the page.
  const headingRef = useRef(null);
  useEffect(() => { if (headingRef.current) headingRef.current.focus(); }, []);
  const acks = Array.isArray(p.acknowledgements) ? p.acknowledgements : [];
  // Every warning once: the collector's warning items (with their detail), then the proposal's
  // warning codes (proposal_warnings and proposal.warnings overlap on an actuals draft).
  const warnings = [];
  const seen = new Set();
  const addWarning = (w) => {
    const code = typeof w === 'string' ? w : w && w.code;
    const key = code || JSON.stringify(w);
    if (seen.has(key)) return;
    seen.add(key);
    warnings.push({ code, detail: typeof w === 'object' && w ? w.detail : null });
  };
  (Array.isArray(preview && preview.warnings) ? preview.warnings : []).forEach(addWarning);
  (Array.isArray(preview && preview.proposal_warnings) ? preview.proposal_warnings : []).forEach(addWarning);
  (Array.isArray(p.warnings) ? p.warnings : []).forEach(addWarning);
  g.warning.forEach((it) => addWarning(it));
  const extras = Array.isArray(p.extras) ? p.extras : [];
  return (
    <div data-testid="tm-approve-confirm">
      <h6 className="mb-2" tabIndex={-1} ref={headingRef} id="tm-approve-confirm-heading">Approve T&amp;M pricing for {orderNumber}?</h6>
      <p className="small text-muted mb-2">
        These prices are written to the order and its quote. Then Create Invoice makes the one close-out invoice.
        Until an invoice exists, Reopen T&amp;M pricing can undo this.
      </p>
      <table className="table table-sm small mb-2">
        <thead className="table-light">
          <tr><th>Line</th><th className="text-end">Ordered → billed</th><th className="text-end">Unit</th><th className="text-end">Line total</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.so_line_id}>
              <td>
                {r.label}
                {r.price_source && <span className="badge bg-success ms-1" style={{ fontSize: '0.6rem' }}>{priceSourceLabel(r.price_source)}</span>}
                {r.no_charge_reason && <div className="text-muted" style={{ fontSize: '0.7rem' }}>no charge: {r.no_charge_reason}</div>}
                {r.override_reason && (
                  <div className="text-warning-emphasis" style={{ fontSize: '0.7rem' }}>
                    own price{r.suggested_unit_price != null ? ` (suggested ${fmtMoney(r.suggested_unit_price)})` : ''}: {r.override_reason}
                  </div>
                )}
              </td>
              <td className="text-end">{r.ordered ?? '—'} → <strong>{r.bill_qty}</strong></td>
              <td className="text-end">{fmtMoney(r.unit_price)}</td>
              <td className="text-end">{fmtMoney(r.line_total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr><td colSpan="3" className="text-end">Subtotal</td><td className="text-end">{fmtMoney(c.subtotal)}</td></tr>
          <tr><td colSpan="3" className="text-end">Shipping</td><td className="text-end">{fmtMoney(h.shipping_amount)}</td></tr>
          <tr><td colSpan="3" className="text-end">Fees</td><td className="text-end">{fmtMoney(h.additional_fees)}</td></tr>
          <tr><td colSpan="3" className="text-end">Discount</td><td className="text-end">−{fmtMoney(h.discount_amount)}</td></tr>
          <tr><td colSpan="3" className="text-end">Tax</td><td className="text-end">{fmtMoney(c.tax_amount)}</td></tr>
          <tr className="fw-bold"><td colSpan="3" className="text-end">Total</td><td className="text-end">{fmtMoney(c.total)}</td></tr>
        </tfoot>
      </table>
      {extras.length > 0 && (
        <div className="small mb-2" data-testid="tm-confirm-extras">
          <strong>Extras in these prices</strong>
          <ul className="mb-0 ps-3">
            {extras.map((e, i) => (
              <li key={i}>
                {e.kind === 'minimum_charge' ? 'Order minimum (a floor)' : String(e.kind).replace(/_/g, ' ')}: {fmtMoney(e.amount)}
                {e.kind === 'minimum_charge' ? `, added ${fmtMoney(e.applied)}` : ''} — {e.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
      {(acks.length > 0 || g.info.length > 0 || warnings.length > 0
        || preview?.delta_vs_estimate != null || preview?.delta_vs_nte != null) && (
        <div className="alert alert-warning py-2 small">
          <strong>Read before approving</strong>
          <ul className="mb-0 ps-3">
            {preview?.delta_vs_estimate != null && <li>Versus the internal estimate: {fmtDelta(preview.delta_vs_estimate)}</li>}
            {preview?.delta_vs_nte != null && <li>Versus the internal not-to-exceed: {fmtDelta(preview.delta_vs_nte)}</li>}
            {acks.map((a) => <li key={`a-${a.code}`}>{chipLabel(a.code)} — acknowledged: {a.reason}</li>)}
            {warnings.map((w, i) => <li key={`w-${i}`}>{chipLabel(w.code)}{w.detail ? ` — ${w.detail}` : ''}</li>)}
            {g.info.map((it) => <li key={`gi-${it.code}`}>{it.label}{it.detail ? ` — ${it.detail}` : ''}</li>)}
          </ul>
        </div>
      )}
      {blocked && <div className="alert alert-danger py-2 small" role="alert">{blocked}</div>}
      <div className="d-flex gap-2 justify-content-end">
        <button type="button" className="btn btn-outline-secondary" onClick={onBack} disabled={busy}>Back</button>
        <button type="button" className="btn btn-success" onClick={onApprove} disabled={busy || !!blocked}>
          {busy ? 'Approving…' : 'Approve T&M Pricing'}
        </button>
      </div>
    </div>
  );
}
